import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { format, parseISO } from "date-fns";
import { Users, ChartLine, CurrencyDollar, BookmarkSimple, Star, Buildings, Books, Sparkle, ArrowUpRight } from "@phosphor-icons/react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar, Cell,
} from "recharts";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function AdminAnalyticsPage() {
  const { user, loading } = useAuth();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [weeks, setWeeks] = useState(12);

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    api.get(`/admin/analytics?weeks=${weeks}`).then((r) => setData(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Couldn't load analytics"));
  }, [user, weeks]);

  const signupSeries = useMemo(() => (data?.users?.weekly_signups || []).map((p) => ({
    label: format(parseISO(p.week_start), "MMM d"),
    signups: p.signups,
  })), [data]);

  if (loading) return <div className="px-6 md:px-10 lg:px-16 py-16">{t("common.loading")}</div>;
  if (!user || user.role !== "admin") {
    return (
      <div data-testid="analytics-noaccess" className="px-6 md:px-10 lg:px-16 py-16 max-w-md mx-auto text-center">
        <h1 className="font-display font-bold text-4xl mb-3">Admin only</h1>
        <Link to="/login" className="btn-primary">Sign in</Link>
      </div>
    );
  }
  if (err) return <div className="px-6 md:px-10 lg:px-16 py-16 text-red-600">{err}</div>;
  if (!data) return <div className="px-6 md:px-10 lg:px-16 py-16 text-zinc-500">{t("common.loading")}</div>;

  const u = data.users;
  const e = data.engagement;
  const c = data.catalog;
  const r = data.revenue;
  const asOf = format(parseISO(data.as_of), "MMM d, yyyy · HH:mm");

  return (
    <div data-testid="admin-analytics-page" className="px-6 md:px-10 lg:px-16 py-12 md:py-16 max-w-7xl">
      <div className="flex items-end justify-between border-b border-zinc-950 pb-4 mb-10 gap-4 flex-wrap">
        <div>
          <div className="overline mb-2">Admin · Analytics</div>
          <h1 className="font-display font-black text-5xl md:text-7xl tracking-[-0.04em] leading-[.92]">Platform pulse</h1>
          <div className="mt-3 text-xs font-mono text-zinc-500">As of {asOf}</div>
        </div>
        <div className="flex items-center gap-2">
          <label className="overline">Window</label>
          <select value={weeks} onChange={(e) => setWeeks(Number(e.target.value))} data-testid="weeks-select" className="border-b border-zinc-300 bg-transparent py-1 text-sm outline-none focus:border-zinc-950">
            {[4, 8, 12, 26, 52].map((w) => <option key={w} value={w}>{w} weeks</option>)}
          </select>
        </div>
      </div>

      {/* Headline KPIs */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200" data-testid="kpi-grid">
        <Kpi label="Registered users" value={u.total} sub={`+${u.joined_7d} this week`} icon={<Users size={18} weight="duotone" />} testid="kpi-users" />
        <Kpi label="Active users" value={u.active} sub={`${u.activation_pct}% activation`} icon={<Sparkle size={18} weight="duotone" />} testid="kpi-active" />
        <Kpi label="Conferences" value={c.conferences} sub={`${c.pending} pending review`} icon={<Buildings size={18} weight="duotone" />} testid="kpi-confs" />
        <Kpi label="Revenue (USD)" value={`$${r.gross_usd.toLocaleString(undefined,{maximumFractionDigits:0})}`} sub={`${r.paid}/${r.checkouts_initiated} paid · ${r.conversion_pct}% conv.`} icon={<CurrencyDollar size={18} weight="duotone" />} testid="kpi-revenue" />
      </section>

      {/* Sign-ups chart */}
      <section className="mt-12 border border-zinc-200 bg-white" data-testid="signups-chart">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <div className="overline mb-1 flex items-center gap-1.5 text-blue-700"><ChartLine size={11} weight="fill" /> Sign-ups</div>
            <h2 className="font-display font-bold text-xl tracking-tight">Weekly registrations</h2>
          </div>
          <div className="text-xs font-mono text-zinc-500">{u.joined_30d} in last 30 days</div>
        </div>
        <div className="px-2 py-6" style={{ height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={signupSeries} margin={{ top: 10, right: 32, left: 8, bottom: 0 }}>
              <defs>
                <linearGradient id="signFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2563EB" stopOpacity={0.32} />
                  <stop offset="100%" stopColor="#2563EB" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#E4E4E7" strokeDasharray="2 4" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono, monospace", fill: "#71717A" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fontFamily: "IBM Plex Mono, monospace", fill: "#71717A" }} axisLine={false} tickLine={false} allowDecimals={false} width={32} />
              <Tooltip cursor={{ stroke: "#09090b", strokeWidth: 1, strokeDasharray: "2 2" }} contentStyle={{ border: "1px solid #09090b", borderRadius: 0, fontFamily: "IBM Plex Mono", fontSize: 12 }} />
              <Area type="monotone" dataKey="signups" stroke="#2563EB" strokeWidth={2} fill="url(#signFill)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Engagement + revenue side-by-side */}
      <div className="mt-12 grid md:grid-cols-12 gap-px bg-zinc-200 border border-zinc-200">
        <Cell12 col={6} title="Engagement" subtitle="What users do once they sign up" testid="engagement-block">
          <Row label="Bookmarks (total)"   value={e.bookmarks}  icon={<BookmarkSimple size={14} />} />
          <Row label="Attending marks"     value={e.attending}  icon={<Sparkle size={14} />} />
          <Row label="Reviews posted"      value={e.reviews}    sub={`${e.reviewers} unique reviewers`} icon={<Star size={14} />} />
          <Row label="Digest subscribers"  value={u.digest_subscribers} icon={<Sparkle size={14} />} />
        </Cell12>
        <Cell12 col={6} title="Revenue (Stripe)" subtitle="Sponsored slots: $99 / $249 / $499 tiers" testid="revenue-block">
          <Row label="Checkouts initiated" value={r.checkouts_initiated} icon={<CurrencyDollar size={14} />} />
          <Row label="Paid (completed)"    value={r.paid} sub={`${r.conversion_pct}% conversion`} icon={<CurrencyDollar size={14} />} />
          <Row label="Gross revenue"       value={`$${r.gross_usd.toLocaleString(undefined,{maximumFractionDigits:2})}`} icon={<CurrencyDollar size={14} />} />
          <Row label="Active sponsored slots" value={c.sponsored} icon={<Sparkle size={14} />} />
        </Cell12>
      </div>

      {/* Top topics + countries (bars) */}
      <div className="mt-12 grid md:grid-cols-2 gap-6">
        <BarBlock title="Top topics" subtitle="Conferences in the catalog by topic" data={c.top_topics.map(x => ({ name: x.topic, value: x.count }))} testid="topics-bars" color="#09090b" />
        <BarBlock title="Top countries" subtitle="Geographic distribution" data={c.top_countries.map(x => ({ name: x.country, value: x.count }))} testid="countries-bars" color="#2563EB" />
      </div>

      {/* Top bookmarked + catalog summary */}
      <div className="mt-12 grid md:grid-cols-12 gap-8">
        <div className="md:col-span-7" data-testid="top-bookmarked">
          <div className="border-b border-zinc-950 pb-3 mb-4 flex items-end justify-between">
            <div>
              <div className="overline mb-1 flex items-center gap-1.5"><BookmarkSimple size={11} /> Most bookmarked</div>
              <h2 className="font-display font-bold text-2xl tracking-tight">Top conferences</h2>
            </div>
            <span className="font-mono text-xs text-zinc-400">{data.top_bookmarked.length}</span>
          </div>
          {data.top_bookmarked.length === 0 ? (
            <div className="border border-zinc-200 p-10 text-center text-zinc-500 text-sm">No bookmarks yet.</div>
          ) : (
            <ul className="divide-y divide-zinc-200 border border-zinc-200">
              {data.top_bookmarked.map((b, i) => (
                <li key={b.id} className="px-5 py-3 flex items-center justify-between hover:bg-[#FAFAFA]">
                  <div className="flex items-center gap-4 min-w-0">
                    <span className="font-mono text-xs text-zinc-400 tabular w-7">#{String(i+1).padStart(2,"0")}</span>
                    <Link to={`/conferences/${b.id}`} className="font-display font-semibold text-sm leading-tight hover:text-blue-700 truncate">{b.title}</Link>
                  </div>
                  <div className="flex items-center gap-4 text-xs font-mono text-zinc-500 flex-shrink-0">
                    <span className="hidden md:inline">{b.topic}</span>
                    <span className="font-semibold text-zinc-950 tabular">{b.bookmarks}</span>
                    <ArrowUpRight size={12} data-arrow="forward" className="text-zinc-300" />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="md:col-span-5" data-testid="catalog-summary">
          <div className="border-b border-zinc-950 pb-3 mb-4">
            <div className="overline mb-1 flex items-center gap-1.5"><Books size={11} /> Catalog</div>
            <h2 className="font-display font-bold text-2xl tracking-tight">Inventory</h2>
          </div>
          <div className="grid grid-cols-2 gap-px bg-zinc-200 border border-zinc-200">
            <MiniCell label="Conferences" v={c.conferences} />
            <MiniCell label="Pending review" v={c.pending} accent={c.pending > 0 ? "#A16207" : null} />
            <MiniCell label="Sponsored slots" v={c.sponsored} accent={c.sponsored > 0 ? "#2563EB" : null} />
            <MiniCell label="Speakers" v={c.speakers} />
            <MiniCell label="Papers" v={c.papers} />
            <MiniCell label="Admins" v={u.admins} />
          </div>
          {c.pending > 0 && (
            <Link to="/admin" data-testid="go-review-pending" className="mt-5 btn-primary inline-flex">Review {c.pending} pending → </Link>
          )}
        </div>
      </div>

      <div className="mt-12 border-t border-zinc-200 pt-4 text-xs font-mono text-zinc-500">
        Refresh the page to fetch the latest numbers · Backend: <code>GET /api/admin/analytics</code>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, icon, testid }) {
  return (
    <div className="bg-white p-6 md:p-7" data-testid={testid}>
      <div className="flex items-center justify-between text-zinc-400 mb-3">
        <div className="overline">{label}</div>
        {icon}
      </div>
      <div className="font-display font-black text-4xl md:text-5xl tracking-tight tabular leading-none">{value}</div>
      <div className="mt-2 text-xs font-mono text-zinc-500">{sub}</div>
    </div>
  );
}

function Cell12({ col, title, subtitle, children, testid }) {
  return (
    <div className={`bg-white p-7 md:col-span-${col}`} data-testid={testid}>
      <div className="overline mb-1">{title}</div>
      <div className="text-xs font-mono text-zinc-500 mb-4">{subtitle}</div>
      <div className="divide-y divide-zinc-100">{children}</div>
    </div>
  );
}

function Row({ label, value, sub, icon }) {
  return (
    <div className="py-3 flex items-center justify-between">
      <div className="flex items-center gap-2.5 text-sm text-zinc-700">
        <span className="text-zinc-400">{icon}</span>
        <span>{label}</span>
      </div>
      <div className="text-end">
        <div className="font-display font-semibold text-lg tracking-tight tabular">{value}</div>
        {sub && <div className="text-[10px] font-mono text-zinc-400">{sub}</div>}
      </div>
    </div>
  );
}

function MiniCell({ label, v, accent }) {
  return (
    <div className="bg-white p-4">
      <div className="font-display font-black text-2xl tabular leading-none" style={accent ? { color: accent } : {}}>{v}</div>
      <div className="overline mt-1">{label}</div>
    </div>
  );
}

function BarBlock({ title, subtitle, data, testid, color }) {
  if (!data || data.length === 0) return null;
  return (
    <section className="border border-zinc-200 bg-white" data-testid={testid}>
      <div className="px-5 py-4 border-b border-zinc-200">
        <div className="overline mb-1">{title}</div>
        <div className="text-xs font-mono text-zinc-500">{subtitle}</div>
      </div>
      <div className="px-2 py-4" style={{ height: 240 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
            <CartesianGrid stroke="#E4E4E7" strokeDasharray="2 4" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono", fill: "#71717A" }} axisLine={false} tickLine={false} allowDecimals={false} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fontFamily: "IBM Plex Sans", fill: "#27272A" }} axisLine={false} tickLine={false} width={110} interval={0} />
            <Tooltip cursor={{ fill: "#FAFAFA" }} contentStyle={{ border: "1px solid #09090b", borderRadius: 0, fontFamily: "IBM Plex Mono", fontSize: 12 }} />
            <Bar dataKey="value" fill={color}>
              {data.map((entry, i) => (<Cell key={i} fill={i === 0 ? "#2563EB" : color} />))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}
